import { ArrowDownOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { dbCols } from '@cc/schema';
import { Create } from '@refinedev/antd';
import { Form, Input, message, Select, Tooltip, Typography } from 'antd';
import {
  useCustom,
  useCustomMutation,
  useWarnAboutChange,
} from '@refinedev/core';
import { useActor } from '@xstate/react';
import qs from 'qs';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'rooks';
import { TherapistActivityReactContext } from '../../App';
import { useClientOptions } from '../../hooks/client-options';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { DownwardArrow } from '../../schema/be-graphql-generated';
import { colors } from '../../styles/colors';
import useStyles from './styles.style';
import { useLocation } from 'react-router-dom';
import { SelectClient } from '../../components/SelectClient';

const { Title } = Typography;

export const DownwardArrowCreate = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const { styles } = useStyles();

  const { clientsLoading } = useClientOptions();

  const { clientId: locationClientId, downwardArrowId } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { innerWidth } = useWindowSize();

  const [clientId, setClientId] = useState<string | undefined>(
    // E57 (Q-e57g1-2 A rule 4): a create page opened with ?clientId= (Create on a client's tool tab, Use a tool) keeps
    // THAT client; the header bar's filter is the fallback
    (locationClientId as string) ||
      therapistActivityState.context.filterClientId,
  );

  useEffect(() => {
    if (
      clientId &&
      therapistActivityState.context.filterClientId !== clientId
    ) {
      therapistActivitySend({
        type: 'ENTITY_CLIENT_ID_CHANGED',
        clientId,
      });
    }
  }, [clientId]);

  const initialValues = useMemo(() => {
    return {
      beliefs: [''] as string[],
    };
  }, [therapistActivityState.context.filterClientId, locationClientId]);

  const [form] = Form.useForm<typeof initialValues>();

  // Refine's unsaved-changes contract: setting `warnWhen` makes the router (and the sider's logout)
  // confirm before leaving, using the existing `warnWhenUnsavedChanges` copy. Same mechanism
  // `profile`, `practice` and `supportingCycles` already rely on.
  const { setWarnWhen } = useWarnAboutChange();

  /** Unsaved work exists — drives BOTH the leave-warning and the Save button's enabled state. */
  const [dirty, setDirty] = useState(false);

  /**
   * The chain always offers EXACTLY ONE empty rung at the bottom — that is how a therapist adds
   * the next thought, and it must hold after loading an existing chain and after saving, not only
   * while typing a fresh one.
   *
   * It is an INVARIANT restored idempotently, not an accumulation. The previous version grew the
   * list from the TextArea's `onChange` via a `debounce(() => add(''), 500)` constructed INSIDE
   * the `Form.List` render callback — so it was a new debounced function on every render, each
   * keystroke started a timer of its own, and several rungs arrived at once (founder, 2026-09-01:
   * "on start of edit there rapidly appear 2 more text areas"). Deriving the blank rather than
   * appending it cannot produce two, however many times it runs.
   */
  const ensureTrailingBlank = useCallback(() => {
    const beliefs: string[] = form.getFieldValue('beliefs') ?? [];
    const isBlank = (b: unknown) => !(typeof b === 'string' && b.trim());
    const next = [...beliefs];
    while (
      next.length > 1 &&
      isBlank(next[next.length - 1]) &&
      isBlank(next[next.length - 2])
    ) {
      next.pop();
    }
    if (next.length === 0 || !isBlank(next[next.length - 1])) {
      next.push('');
    }
    if (next.length !== beliefs.length) {
      // `setFieldsValue` does not re-enter `onValuesChange`, so this cannot loop
      form.setFieldsValue({ beliefs: next });
    }
  }, [form]);

  const [arrowId, setArrowId] = useState(downwardArrowId as string | undefined);

  useActivityTracking(arrowId ? arrowId : `${dbCols.downwardArrows}/create`);

  const {
    data: downwardArrowData,
    isLoading: downwardArrowLoading,
    refetch: refetchdownwardArrow,
    error: downwardArrowError,
  } = useCustom<DownwardArrow>({
    url: '',
    method: 'get',
    meta: {
      operation: 'downwardArrow',
      fields: ['_id', 'beliefs', 'userId'],
      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: arrowId,
        },
      },
    },
  });

  useEffect(() => {
    if (downwardArrowData?.data) {
      form?.setFieldsValue({
        beliefs: downwardArrowData?.data?.beliefs || [''],
      });
      // a loaded chain gets its empty rung too, or there is no way to extend it (founder, 2026-09-01:
      // "not possible to add a new text block when you edit an existing one")
      ensureTrailingBlank();
      setDirty(false); // loading is not editing
      setClientId(downwardArrowData?.data.userId);
      if (downwardArrowData?.data?._id) {
        therapistActivitySend({
          type: 'LOADED',
          resource: downwardArrowData?.data?._id,
          clientId: downwardArrowData?.data?.userId,
        });
      }
    }
  }, [downwardArrowData]);

  const {
    mutateAsync: upsertDownwardArrow,
    data: upsertDownwardArrowData,
    isLoading: upsertDownwardArrowLoading,
  } = useCustomMutation<{ downwardArrow: DownwardArrow }>();

  /**
   * `mutateAsync`, not `mutate`: with explicit save the caller MUST know whether the write landed.
   * `mutate` is fire-and-forget — awaiting it does nothing — so the old code announced success
   * unconditionally, and now that a save also clears the unsaved-work guard, a failed one would
   * quietly drop the therapist's protection while telling them it had saved.
   */
  const saveData = useCallback(
    async (data: typeof initialValues) => {
      if (!clientId) {
        return;
      }
      await upsertDownwardArrow({
        url: '',
        method: 'post',
        values: {},
        meta: {
          operation: 'upsertDownwardArrow',
          fields: ['_id'],
          variables: {
            input: {
              name: 'input',
              type: 'DownwardArrowInput',
              value: {
                data,
                where: {
                  clientId,
                  _id: arrowId,
                },
              },
            },
          },
        },
      });
      message.success(t('shared.downwardArrow.saveSuccess'));
    },
    [clientId, arrowId, t],
  );

  useEffect(() => {
    // @ts-ignore
    if (upsertDownwardArrowData?.data?._id) {
      if (!arrowId) {
        therapistActivitySend({
          type: 'CREATED',
          // @ts-ignore
          resource: upsertDownwardArrowData?.data?._id,
        });

        // @ts-ignore
        setArrowId(upsertDownwardArrowData?.data?._id);
      } else {
        therapistActivitySend({
          type: 'UPDATED',
          resource: arrowId,
        });
      }
    }
  }, [upsertDownwardArrowData]);

  /**
   * EXPLICIT SAVE (founder decision, 2026-09-01). This form used to autosave every change on a
   * 700 ms debounce with no Save button, which was wrong in three separate ways:
   *
   *   • it wrote the therapist's keystrokes into the event log — one event per typing pause, each
   *     carrying the full clinical text, so the stream recorded drafts instead of decisions;
   *   • `arrowId` is only set when the FIRST save's response returns, so a second debounced save
   *     firing before it inserted a SECOND record for one authored chain (a legacy bug, not an
   *     ES one — the therapist branch INSERTs whenever `_id` is null);
   *   • there was no way for the therapist to say "I have finished thinking".
   *
   * A save is now a deliberate act, and leaving with unsaved work warns first — the same
   * `warnWhenUnsavedChanges` contract `profile`, `practice` and `supportingCycles` already use,
   * which the sider honours on logout.
   */
  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
    ensureTrailingBlank();
  }, [setWarnWhen, ensureTrailingBlank]);

  const onSave = useCallback(async () => {
    const { beliefs = [] } = form.getFieldsValue();
    try {
      // the trailing blank is an editing affordance, not a rung — the decider drops blanks anyway,
      // but the legacy path stores what it is given, so strip it here and both eras agree
      await saveData({ beliefs: beliefs.filter((b) => b?.trim()) });
    } catch {
      // the work is still unsaved: keep the guard up and leave Save offered
      return;
    }
    setDirty(false);
    setWarnWhen(false); // saved — leaving is safe again
    ensureTrailingBlank(); // and the next thought still has somewhere to go
  }, [saveData, form, setWarnWhen, ensureTrailingBlank]);

  return (
    <Create
      title={
        <>
          {t('shared.downwardArrow.title')}

          <Tooltip
            title={t('shared.downwardArrow.description')}
            overlayClassName={styles['downward-arrow--tooltip']}
          >
            <QuestionCircleOutlined style={{ margin: '0 10px' }} />
          </Tooltip>
        </>
      }
      isLoading={clientsLoading}
      saveButtonProps={{
        'onClick': onSave,
        'loading': upsertDownwardArrowLoading,
        // nothing to save is not a thing to offer: the button follows the dirty flag that also
        // drives the leave-warning, so "enabled" and "you would lose work" mean the same thing
        'disabled': !clientId || !dirty,
        'data-testid': 'cc-downward-arrow--save',
      }}
    >
      <div className={styles['downward-arrow-container']}>
        <SelectClient clientId={clientId} setClientId={setClientId} />
      </div>

      <Form<typeof initialValues>
        form={form}
        initialValues={{
          ...initialValues,
        }}
        onFinish={(values) => {}}
        onValuesChange={onValuesChange}
        labelCol={{ span: 7 }}
        wrapperCol={{ span: 16 }}
        labelWrap
        data-testid="cc-create-downward-arrow-container"
      >
        <Form.List
          name="beliefs"
          rules={[
            {
              validator: async (_, beliefs) => {
                if (!beliefs || beliefs.length < 2) {
                  return Promise.reject(new Error('At least 2 beliefs'));
                }
              },
            },
          ]}
        >
          {(fields) => {
            return (
              <>
                {fields.map((field, index) => {
                  return (
                    <>
                      <Form.Item
                        {...field}
                        style={{ flex: 1 }}
                        label={t('shared.downwardArrow.thought')}
                      >
                        <Input.TextArea
                          data-testid={`cc-downward-arrow--thought-input-${index}`}
                          id={`field$-${index}`}
                          placeholder={t('shared.downwardArrow.thought')}
                          style={{ width: '60%' }}
                          autoSize={{ minRows: 4, maxRows: 10 }}
                          // The rung that grows the chain is added by `ensureTrailingBlank`, not
                          // here. This used to call a `debounce(() => add(''), 500)` created
                          // INSIDE the render callback — so it was rebuilt every render and never
                          // debounced anything: each keystroke started its own timer and several
                          // empty rungs appeared at once.
                        />
                      </Form.Item>
                      {fields.length >= index + 2 && (
                        <div
                          className={
                            styles['downward-arrow--question-container']
                          }
                        >
                          <ArrowDownOutlined color={colors['--triad-blue-0']} />
                          <Title level={5}>
                            {t('shared.downwardArrow.question')}
                          </Title>
                          <ArrowDownOutlined color={colors['--triad-blue-0']} />
                        </div>
                      )}
                    </>
                  );
                })}
              </>
            );
          }}
        </Form.List>
      </Form>
    </Create>
  );
};
